import { useState } from "react";
import { useUser, useReverification } from "@clerk/react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, KeyRound, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";

const clerkErr = (e) => e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message || e?.message || "Erreur";

const BackupCodes = ({ codes, onDone }) => {
  const { t } = useApp();
  return (
    <div className="space-y-3" data-testid="mfa-backup-codes">
      <p className="text-sm font-semibold text-slate-800">{t("mfa_backup_title")}</p>
      <p className="text-xs text-slate-500">{t("mfa_backup_help")}</p>
      <div className="grid grid-cols-2 gap-2 rounded-lg border bg-slate-50 p-3 num text-sm" dir="ltr">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success(t("copied")); }} data-testid="mfa-copy-codes"><Copy className="h-4 w-4 me-1" />{t("copy")}</Button>
        <Button onClick={onDone} data-testid="mfa-finish">{t("mfa_finish")}</Button>
      </div>
    </div>
  );
};

const TotpFlow = ({ onEnrolled }) => {
  const { t } = useApp();
  const { user } = useUser();
  const [totp, setTotp] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const createTOTP = useReverification(() => user.createTOTP());
  const begin = async () => {
    try { setTotp(await createTOTP()); } catch (e) { toast.error(clerkErr(e)); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await user.verifyTOTP({ code: code.trim() });
      let codes = [];
      try { codes = (await user.createBackupCode()).codes; } catch { codes = []; }
      await user.reload();
      onEnrolled(codes);
    } catch (e) { toast.error(clerkErr(e)); }
    setBusy(false);
  };
  if (!totp) return <Button onClick={begin} className="w-full" data-testid="mfa-totp-start"><KeyRound className="h-4 w-4 me-1" />{t("mfa_totp")}</Button>;
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">{t("mfa_scan")}</p>
      <div className="flex justify-center rounded-lg border bg-white p-3" data-testid="mfa-qr"><QRCodeSVG value={totp.uri} size={180} /></div>
      <p className="break-all text-center num text-[11px] text-slate-400" dir="ltr">{totp.secret}</p>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="123456" dir="ltr" data-testid="mfa-totp-code" />
        <Button onClick={verify} disabled={busy || code.trim().length < 6} data-testid="mfa-totp-verify">{t("mfa_verify")}</Button>
      </div>
    </div>
  );
};

const SmsFlow = ({ onEnrolled }) => {
  const { t } = useApp();
  const { user } = useUser();
  const [phone, setPhone] = useState("+216");
  const [res, setRes] = useState(null);
  const [code, setCode] = useState("");
  const createPhone = useReverification((n) => user.createPhoneNumber({ phoneNumber: n }));
  const send = async () => {
    try {
      const p = (await createPhone(phone.replace(/\s/g, ""))) || user.phoneNumbers.find((x) => x.phoneNumber === phone.replace(/\s/g, ""));
      await p.prepareVerification();
      setRes(p);
    } catch (e) { toast.error(clerkErr(e)); }
  };
  const verify = async () => {
    try {
      const r = await res.attemptVerification({ code: code.trim() });
      await r.setReservedForSecondFactor({ reserved: true });
      await user.reload();
      onEnrolled([]);
    } catch (e) { toast.error(clerkErr(e)); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" disabled={!!res} data-testid="mfa-sms-phone" />
        <Button variant="outline" onClick={send} disabled={!!res} data-testid="mfa-sms-send"><Smartphone className="h-4 w-4 me-1" />{t("mfa_send_code")}</Button>
      </div>
      {res && <div className="flex gap-2"><Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} dir="ltr" data-testid="mfa-sms-code" /><Button onClick={verify} data-testid="mfa-sms-verify">{t("mfa_verify")}</Button></div>}
    </div>
  );
};

export const MfaEnroll = ({ onDone }) => {
  const { me, refreshMe, t } = useApp();
  const [codes, setCodes] = useState(null);
  const av = me?.mfa?.available || [];
  const enrolled = async (c) => { await refreshMe(); if (c.length) setCodes(c); else onDone?.(); };
  if (codes) return <BackupCodes codes={codes} onDone={onDone} />;
  return (
    <div className="space-y-4" data-testid="mfa-enroll">
      {av.includes("totp") && <TotpFlow onEnrolled={enrolled} />}
      {av.includes("totp") && av.includes("phone_code") && <div className="text-center text-xs text-slate-400">{t("or")}</div>}
      {av.includes("phone_code") && <SmsFlow onEnrolled={enrolled} />}
    </div>
  );
};

export const MfaGate = () => {
  const { t } = useApp();
  const { user } = useUser();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" data-testid="mfa-gate">
      <div className="w-full max-w-md space-y-5 rounded-2xl border bg-white p-8 shadow-sm">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50"><ShieldCheck className="h-6 w-6 text-teal-700" /></div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{t("mfa_gate_title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("mfa_gate_help")}</p>
          <p className="mt-1 text-xs text-slate-400">{user?.primaryEmailAddress?.emailAddress}</p>
        </div>
        <MfaEnroll onDone={() => window.location.reload()} />
      </div>
    </div>
  );
};
